<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const loading = ref({});
const searchQuery = ref('');
const loadError = ref(false);

const teamsList = useMapGetter('teams/getTeams');

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return teamsList.value;
  return picoSearch(teamsList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const loadTeams = async () => {
  loadError.value = false;
  try {
    await store.dispatch('teams/get');
  } catch {
    loadError.value = true;
  }
};

onMounted(loadTeams);

const deleteTeam = async ({ id }) => {
  if (loading.value[id]) return false;

  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
    return true;
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
    return false;
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = async () => {
  const teamToDelete = selectedTeam.value;
  const teamId = teamToDelete.id;
  if (loading.value[teamId]) return;

  const deleted = await deleteTeam(teamToDelete);
  if (deleted && selectedTeam.value === teamToDelete) closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="!teamsList.length && !loadError"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('TEAMS_SETTINGS.HEADER')"
        :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
        :link-text="$t('TEAMS_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="team_management"
      >
        <template v-if="teamsList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link
            v-if="isAdmin"
            :to="{ name: 'settings_teams_new' }"
            class="inline-flex min-h-11 items-center justify-center rounded-xl bg-n-brand px-4 text-sm font-medium text-white hover:bg-n-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          >
            {{ $t('TEAMS_SETTINGS.NEW_TEAM') }}
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="loadError"
        role="alert"
        class="flex items-center justify-between gap-3 rounded-xl border border-n-weak p-3 text-sm text-n-slate-12"
      >
        <span>{{ $t('TEAMS_SETTINGS.LIST.LOAD_ERROR') }}</span>
        <Button
          :label="$t('TEAMS_SETTINGS.LIST.RETRY')"
          :disabled="uiFlags.isFetching"
          @click="loadTeams"
        />
      </div>
      <span
        v-if="!filteredTeamsList.length && searchQuery"
        role="status"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('TEAMS_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="team in filteredTeamsList"
          :key="team.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex min-w-0 flex-1 items-start gap-3">
            <div
              class="flex items-center flex-shrink-0 size-10 justify-center rounded-2xl outline outline-1 outline-n-weak -outline-offset-1"
            >
              <Icon icon="i-ph-users" class="size-4 text-n-slate-11" />
            </div>
            <div class="flex min-w-0 flex-1 flex-col items-start gap-1">
              <span
                class="block max-w-full break-words text-heading-3 text-n-slate-12"
              >
                {{ team.name }}
              </span>
              <p
                class="mb-0 max-w-full break-words text-n-slate-11 text-body-main"
              >
                {{ team.description }}
              </p>
            </div>
          </div>
          <div class="flex shrink-0 justify-end gap-1">
            <router-link
              v-if="isAdmin"
              :to="{
                name: 'settings_teams_edit',
                params: { teamId: team.id },
              }"
              :aria-label="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
              :title="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
              class="inline-flex size-11 items-center justify-center rounded-xl text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            >
              <Icon
                icon="i-ph-pencil-simple"
                class="size-4"
                aria-hidden="true"
              />
            </router-link>

            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
              icon="i-ph-trash"
              :aria-label="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
              slate
              sm
              class="!size-11 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :is-loading="loading[team.id]"
              @click="openDelete(team)"
            />
          </div>
        </div>
      </div>
    </template>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
